import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title: string;
  /** The section a screen sits in, said small above its heading. */
  eyebrow?: string;
  /**
   * The way back to the screen this one sits under — the app's own link,
   * drawn above everything else in the header.
   */
  back?: ReactNode;
  /**
   * A status that qualifies the heading rather than an action on it — Rooms'
   * `READ-ONLY`, which is a property of the whole screen. It sits beside the
   * title rather than in `actions` because the actions slot is pushed to the
   * far side of the row, and a badge stranded there reads as a control.
   */
  badge?: ReactNode;
  /**
   * A node rather than a string, so a screen whose subtitle is an identifier can
   * set it in mono and put a status badge beside it — which the player inspector
   * is the first screen to need, and which is the design's own treatment of a
   * heading whose subject is a UUID.
   */
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Why the screen's primary action cannot be pressed.
   *
   * Stated **visibly**, beside the control, in `meta`/`ink-3` — not a tooltip,
   * not silence. This is the obligation a `blocked` empty state carries on the
   * screen around it: a body explaining that a shop cannot be priced while the
   * **New shop** button stays fully accented is a screen contradicting itself
   * in two places at once, which is what Shops shipped.
   *
   * The caller still has to pass the action `disabled`. This states the reason;
   * it cannot enforce it, and a reason next to a live button would be worse
   * than either.
   */
  actionsBlockedBecause?: ReactNode;
}

/** A screen's heading, its one sentence, and what can be done from here. */
export function PageHeader({
  className,
  title,
  eyebrow,
  back,
  badge,
  description,
  actions,
  actionsBlockedBecause,
  ...props
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "mb-7 flex flex-wrap items-center justify-between gap-x-6 gap-y-4",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1 basis-64">
        {back ? <div className="mb-3">{back}</div> : null}
        {/* `text-display` rather than a raw Tailwind size: the type scale exists to hold
            exactly this, and the heading was the one element on the page still
            setting its own size. It carries the tracking and line height with
            it, so a page heading is one decision rather than three classes that
            each screen could get slightly wrong. */}
        {eyebrow ? (
          <p className="mb-2 font-mono text-micro tracking-[0.08em] text-ink-3 uppercase">
            {eyebrow}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-display font-semibold text-balance text-ink">{title}</h1>
          {badge}
        </div>
        {/* Capped at a reading measure. Without it a long description takes the
            full width of the canvas, which is both a hard line to read and
            enough to push the actions onto their own row — Economy health's
            two-clause sentence did exactly that to its one button. */}
        {description ? (
          <p className="mt-2 max-w-2xl text-ui text-ink-3">{description}</p>
        ) : null}
      </div>
      {/* `shrink-0` is right — an action squeezed into an ellipsis is worse
          than one on its own line — but on its own it sizes this row to its
          content and lets two actions run off the side of a narrow screen.
          Overview puts a segmented range picker and a button here, which
          measured 379px against 350 available at 390px wide and scrolled the
          whole document sideways. `max-w-full` is what gives `flex-wrap`
          something to wrap against: without a cap there is no overflow to
          resolve, so it never fires. */}
      {actions || actionsBlockedBecause ? (
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
          {actionsBlockedBecause ? (
            <span className="max-w-40 text-right text-meta text-ink-3">
              {actionsBlockedBecause}
            </span>
          ) : null}
          {actions}
        </div>
      ) : null}
    </header>
  );
}
